/**
 * ProfileCompletionBanner
 * Shows a persistent warning banner at the top of all pages when the user's
 * profile is incomplete. Disappears once all mandatory fields are filled.
 */
import { useMemo } from 'react';
import { Box, Typography, Alert, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonIcon from '@mui/icons-material/Person';

// Fields required for a complete profile (matches Profile page logic)
const REQUIRED_FIELDS = [
  { key: 'contactNumber', label: 'Contact Number', alt: 'phone' },
  { key: 'department', label: 'Department' },
  { key: 'jobLocation', label: 'Job Location', alt: 'jobLocationId' },
  { key: 'employeeId', label: 'Employee ID', alt: 'idCardNumber' },
  { key: 'designation', label: 'Designation' },
  { key: 'reportingManagerId', label: 'Reporting Manager' },
];

function isFieldFilled(user, field) {
  const val = user?.[field.key];
  if (val) return true;
  // Check alternate field name
  if (field.alt && user?.[field.alt]) return true;
  return false;
}

export function useProfileComplete() {
  const user = useAuthStore((s) => s.user);

  return useMemo(() => {
    if (!user) return { isComplete: true, missingFields: [], percentage: 100 };

    const missing = REQUIRED_FIELDS.filter((f) => !isFieldFilled(user, f));
    const filled = REQUIRED_FIELDS.length - missing.length;
    const percentage = Math.round((filled / REQUIRED_FIELDS.length) * 100);

    return {
      isComplete: missing.length === 0,
      missingFields: missing,
      percentage,
    };
  }, [user]);
}

export default function ProfileCompletionBanner() {
  const navigate = useNavigate();
  const { isComplete, missingFields, percentage } = useProfileComplete();

  if (isComplete) return null;

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 2.5, md: 3 },
        pt: 1.5,
        maxWidth: '1600px',
        width: '100%',
        mx: 'auto',
      }}
    >
      <Alert
        severity="warning"
        icon={<WarningAmberIcon />}
        sx={{
          borderRadius: 1.5,
          alignItems: 'center',
          '& .MuiAlert-message': { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 },
        }}
        action={
          <Button
            color="warning"
            size="small"
            variant="outlined"
            startIcon={<PersonIcon />}
            onClick={() => navigate('/profile')}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Complete Profile
          </Button>
        }
      >
        <Box>
          <Typography variant="body2" fontWeight={600}>
            Your profile is incomplete ({percentage}% done)
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Missing: {missingFields.map((f) => f.label).join(', ')}. Please complete your profile to use all features.
          </Typography>
        </Box>
      </Alert>
    </Box>
  );
}
